import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchACHFundTransaction from '@/hooks/useApi/useFetchACHFundTransaction';
import AchFundingTransactionInfo from './ACHFundingTransactionInfo';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { isPaymentSubTransaction } from './isPaymentSubTransaction';

type AchFundingTransactionProps = {
  TransactionID: string;
} & Omit<
  ComponentProps<typeof AchFundingTransactionInfo>,
  'transaction' | 'subTransactionId' | 'subTransactionType' | 'subTransactionStatus'
>;

export default function AchFundingTransactionContainer({ TransactionID, jwt, ...props }: AchFundingTransactionProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { achFundTransaction, fetchAchFundTransaction, resetAchFundTransaction } = useFetchACHFundTransaction({
    bearer: jwt,
  });

  useEffect(() => {
    blockingLoader.show();
    resetAchFundTransaction();

    fetchAchFundTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) => {
        // Not returned: the dialog's promise settles on dismissal, and the loader must drop before that.
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred'));
      })
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = achFundTransaction;

  // A funding transaction can fund a payment on any rail, not just its own — don't assume the
  // child is an ACH Withdrawal, just skip Fee (Credit Card funding creates a sibling Fee child).
  const withdrawalSubTransaction = Object.values(transaction?.SubTransactions || {}).find(isPaymentSubTransaction);

  return (
    <div>
      {transaction && (
        <AchFundingTransactionInfo
          transaction={transaction}
          jwt={jwt}
          subTransactionId={withdrawalSubTransaction?.TransactionID}
          subTransactionType={withdrawalSubTransaction?.TransactionType}
          subTransactionStatus={withdrawalSubTransaction?.TransactionStatus}
          {...props}
        />
      )}
    </div>
  );
}
